import { supabase } from "@/integrations/supabase/client";

export type CompanyPolicyRequirementKey = "license" | "insurance" | "w9";
export type CompanyPolicyRequirement = Readonly<{
  requirementKey: CompanyPolicyRequirementKey;
  displayLabel?: string;
  description?: string | null;
  enabled: boolean;
  required: boolean;
  expirationRequired: boolean;
  issueDateRequired: boolean;
  instructions?: string | null;
  sortOrder: number;
}>;
export type CompanyPolicyDraft = Readonly<{
  id: string; organizationId: string; basePolicyVersionId: string | null;
  revision: number; requirements: readonly CompanyPolicyRequirement[];
  versionNote: string | null; status: string; updatedAt: string;
}>;
export type CompanyPolicyVersion = Readonly<{
  id: string; versionNumber: number; status: string; publishedAt: string | null;
  publishedBy: string | null; publishedByLabel: string | null; versionNote: string | null;
  requirements: readonly CompanyPolicyRequirement[];
}>;
export type CompanyPolicyImpact = Readonly<{
  draftId: string; draftRevision: number; currentPolicyVersionId: string | null;
  requirementsAdded: readonly CompanyPolicyRequirementKey[];
  requirementsRemoved: readonly CompanyPolicyRequirementKey[];
  rulesChanged: readonly CompanyPolicyRequirementKey[];
  affectedCompanyRecordCount: number;
  currentlyOperationallyVerifiedRecordCount: number;
  recordsThatMayBecomeInProgress: number;
  recordsMissingNewRequirement: number;
  existingEvidenceMayRemainUsable: number;
  existingEvidenceCannotSatisfyNewRules: number;
  previewToken: string;
}>;

export class CompanyPolicyClientError extends Error {
  constructor(readonly code: string, readonly status: number | null) { super(code); }
}
function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
function status(error: unknown): number | null {
  const context = record(record(error).context); const value = Number(context.status ?? record(error).status);
  return Number.isInteger(value) ? value : null;
}
async function invoke(route: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const result = await supabase.functions.invoke(route, { body });
  if (result.error) {
    const response = record(result.data); const code = typeof response.code === "string"
      ? response.code : "company_policy_service_unavailable";
    throw new CompanyPolicyClientError(code, status(result.error));
  }
  return record(result.data);
}
function requirement(value: unknown): CompanyPolicyRequirement {
  const row = record(value); const key = String(row.requirementKey);
  if (key !== "license" && key !== "insurance" && key !== "w9") throw new Error("company_policy_response_invalid");
  return { requirementKey: key, displayLabel: typeof row.displayLabel === "string" ? row.displayLabel : undefined,
    description: typeof row.description === "string" ? row.description : null, enabled: row.enabled !== false,
    required: row.required === true, expirationRequired: row.expirationRequired === true,
    issueDateRequired: row.issueDateRequired === true,
    instructions: typeof row.instructions === "string" ? row.instructions : null,
    sortOrder: Number(row.sortOrder) };
}
function draft(value: unknown): CompanyPolicyDraft | null {
  if (value == null) return null; const row = record(value);
  return { id: String(row.id), organizationId: String(row.organizationId),
    basePolicyVersionId: row.basePolicyVersionId ? String(row.basePolicyVersionId) : null,
    revision: Number(row.revision), requirements: Array.isArray(row.requirements) ? row.requirements.map(requirement) : [],
    versionNote: typeof row.versionNote === "string" ? row.versionNote : null,
    status: String(row.status), updatedAt: String(row.updatedAt) };
}
function version(value: unknown): CompanyPolicyVersion {
  const row = record(value);
  return { id: String(row.id), versionNumber: Number(row.versionNumber), status: String(row.status),
    publishedAt: row.publishedAt ? String(row.publishedAt) : null,
    publishedBy: row.publishedBy ? String(row.publishedBy) : null,
    publishedByLabel: typeof row.publishedByLabel === "string" ? row.publishedByLabel : null,
    versionNote: typeof row.versionNote === "string" ? row.versionNote : null,
    requirements: Array.isArray(row.requirements) ? row.requirements.map(requirement) : [] };
}
function requirementKeys(value: unknown): CompanyPolicyRequirementKey[] {
  if (!Array.isArray(value)) throw new Error("company_policy_response_invalid");
  return value.map((item) => {
    if (item !== "license" && item !== "insurance" && item !== "w9") throw new Error("company_policy_response_invalid");
    return item;
  });
}
function impact(value: unknown): CompanyPolicyImpact {
  const row = record(value);
  const previewToken = String(row.previewToken ?? "");
  if (!/^[0-9a-f]{64}$/i.test(previewToken)) throw new Error("company_policy_response_invalid");
  return { draftId: String(row.draftId), draftRevision: Number(row.draftRevision),
    currentPolicyVersionId: row.currentPolicyVersionId ? String(row.currentPolicyVersionId) : null,
    requirementsAdded: requirementKeys(row.requirementsAdded),
    requirementsRemoved: requirementKeys(row.requirementsRemoved), rulesChanged: requirementKeys(row.rulesChanged),
    affectedCompanyRecordCount: Number(row.affectedCompanyRecordCount),
    currentlyOperationallyVerifiedRecordCount: Number(row.currentlyOperationallyVerifiedRecordCount),
    recordsThatMayBecomeInProgress: Number(row.recordsThatMayBecomeInProgress),
    recordsMissingNewRequirement: Number(row.recordsMissingNewRequirement),
    existingEvidenceMayRemainUsable: Number(row.existingEvidenceMayRemainUsable),
    existingEvidenceCannotSatisfyNewRules: Number(row.existingEvidenceCannotSatisfyNewRules),
    previewToken };
}

export const CompanyCompliancePolicyService = {
  async getWorkspace(organizationId: string) {
    const [activeResponse, draftResponse, historyResponse, catalogResponse] = await Promise.all([
      invoke("company-compliance-policy-read", { operation: "get_active_policy", organizationId }),
      invoke("company-compliance-policy-read", { operation: "get_draft_policy", organizationId }),
      invoke("company-compliance-policy-read", { operation: "get_policy_history", organizationId }),
      invoke("company-compliance-policy-read", { operation: "list_catalog", organizationId }),
    ]);
    return { active: activeResponse.policy ? version(activeResponse.policy) : null,
      draft: draft(draftResponse.draft),
      history: Array.isArray(historyResponse.items) ? historyResponse.items.map(version) : [],
      catalog: Array.isArray(catalogResponse.items) ? catalogResponse.items.map((item) => record(item)) : [] };
  },
  async createDraft(organizationId: string) {
    return draft((await invoke("company-compliance-policy-manage", { operation: "create_draft", organizationId })).draft)!;
  },
  async updateDraft(organizationId: string, value: CompanyPolicyDraft) {
    return draft((await invoke("company-compliance-policy-manage", { operation: "update_draft", organizationId,
      draftId: value.id, expectedRevision: value.revision, requirements: value.requirements,
      versionNote: value.versionNote })).draft)!;
  },
  async discardDraft(organizationId: string, value: CompanyPolicyDraft) {
    await invoke("company-compliance-policy-manage", { operation: "discard_draft", organizationId,
      draftId: value.id, expectedRevision: value.revision });
  },
  async preview(organizationId: string, value: CompanyPolicyDraft): Promise<CompanyPolicyImpact> {
    return impact(await invoke("company-compliance-policy-read", { operation: "preview_policy_publish", organizationId,
      draftId: value.id, expectedRevision: value.revision }));
  },
  async publish(organizationId: string, value: CompanyPolicyDraft, impact: CompanyPolicyImpact) {
    return invoke("company-compliance-policy-manage", { operation: "publish_policy_version", organizationId,
      draftId: value.id, expectedRevision: value.revision, previewToken: impact.previewToken,
      idempotencyKey: crypto.randomUUID() });
  },
};
